import { Box } from '@mui/material';
import MuiAvatar from '@mui/material/Avatar';
import React, { forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DIGITAL_BUDGET_WARNING_RATE } from '../../../constants/shell';
import { INITIAL_PAGE_SIZE, PER_PAGE_OPTIONS } from '../../../constants/table';
import { selectIsUserBudgetEnabled } from '../../../store/selectors/organizations';
import { IUser } from '../../../types/users';
import { getFullName } from '../../../utils/helpers';
import { getUserRoleLabel } from '../../../utils/users';
import { Budget, Card } from '../../index';
import PaginatedList, { IPaginatedListChangeEvent, IPaginatedListRef } from '../../PaginatedList/PaginatedList';
import { PageContent, PaginationControls, TableFilteredEmptyPlaceholder } from '../../_mui';

import styles from './UsersGridView.module.scss';

interface IProps {
  users: IUser[] | null;
  totalCount: number;
  pageSize?: number;
  onChange: (event: IPaginatedListChangeEvent) => void;
  onPageSizeChange?: (newPageSize: number) => void;
  page?: number;

  getDepartmentLabel: (id: string) => string | undefined;
  handleUserClick: (user: IUser) => void;
  renderIntegrations: (_: Partial<IUser>) => React.ReactNode;
  checkCanEditUser: (user: IUser) => boolean;
  renderControls: (user: IUser) => React.ReactNode;
  renderSkipCrmCheckbox: (user: IUser) => React.ReactNode;
  onClearFilters: () => void;
}

const UsersGridView = forwardRef<IPaginatedListRef<IUser>, IProps>(
  (
    {
      users,
      totalCount,
      pageSize = INITIAL_PAGE_SIZE,
      onChange,
      onPageSizeChange,
      page,
      getDepartmentLabel,
      handleUserClick,
      renderIntegrations,
      checkCanEditUser,
      renderControls,
      renderSkipCrmCheckbox,
      onClearFilters,
    },
    paginatedListRef,
  ) => {
    const isUserBudgetEnabled = useSelector(selectIsUserBudgetEnabled);

    const placeholder = useMemo(
      () => (
        <PageContent>
          <TableFilteredEmptyPlaceholder itemType="users" onClearFilters={onClearFilters} />
        </PageContent>
      ),
      [onClearFilters],
    );

    return (
      <Box className={styles.gridViewContainer}>
        <PaginatedList<IUser>
          ref={paginatedListRef}
          items={users || []}
          pageSize={pageSize}
          totalCount={totalCount}
          onChange={onChange}
          defaultValue={page}
        >
          {({ items: usersPage, pagination: Pagination }) => {
            const hasVisibleUsers = usersPage.length > 0;

            return usersPage.length === 0 ? (
              placeholder
            ) : (
              <>
                <PageContent>
                  <div className={styles.list}>
                    {usersPage.map((user: IUser) => {
                      const {
                        uid,
                        first_name,
                        last_name,
                        email,
                        picture,
                        used_budget,
                        available_budget,
                        budget_currency,
                        admin_type,
                        department_id,
                        integration,
                      } = user;

                      const fullName = getFullName(first_name, last_name);
                      const firstLetter = (fullName || email)?.trim()?.[0]?.toUpperCase();
                      return (
                        <Card
                          className={styles.card}
                          key={uid}
                          onClick={() => handleUserClick(user)}
                          top={
                            <>
                              <MuiAvatar
                                src={picture}
                                alt={fullName || email}
                                slotProps={{
                                  img: {
                                    referrerPolicy: 'no-referrer-when-downgrade',
                                  },
                                }}
                                className={styles.avatar}
                              >
                                {firstLetter}
                              </MuiAvatar>
                              <div className={styles.userInfo}>
                                <span className={styles.username}>
                                  {first_name} {last_name}
                                </span>
                                <span className={styles.email}>{email}</span>
                              </div>
                              <div className={styles.roleDeptInfo}>
                                <span className={styles.department}>{getDepartmentLabel(department_id)}</span>
                                <span className={styles.role}>{getUserRoleLabel(admin_type)}</span>
                              </div>
                            </>
                          }
                          middle={
                            <React.Fragment>
                              {renderIntegrations({ integration, uid })}
                              {isUserBudgetEnabled && (
                                <Budget
                                  className={styles.budget}
                                  usedBudget={used_budget}
                                  availableBudget={available_budget}
                                  fixedDecimalScale={false}
                                  lowBudgetRate={DIGITAL_BUDGET_WARNING_RATE}
                                  budgetCurrency={budget_currency}
                                />
                              )}
                            </React.Fragment>
                          }
                          bottom={checkCanEditUser(user) ? renderControls(user) : renderSkipCrmCheckbox(user)}
                          topClassName={styles.top}
                          middleClassName={styles.middle}
                          bottomClassName={styles.controls}
                        />
                      );
                    })}
                  </div>
                </PageContent>

                {hasVisibleUsers && (
                  <PaginationControls
                    pageSize={pageSize}
                    options={PER_PAGE_OPTIONS}
                    onPageSizeChange={onPageSizeChange!}
                    PaginationComponent={Pagination}
                  />
                )}
              </>
            );
          }}
        </PaginatedList>
      </Box>
    );
  },
);

export default UsersGridView;
